import { ModalProps } from '@/types/shared'
import { Button, Dialog } from 'vnetwork-ui'
import { useTranslation } from 'react-i18next'
import { useForm } from 'react-hook-form'
import { FormItem } from 'vnetwork-ui'
import { useMutationWithHandler } from '@/hooks/useMutationWithHandler'
import { rollbackInvoice } from '@/modules/Invoice/services'
import useQueryClientCallback from '@/hooks/useQueryClientCallback'
import { QUERY_KEYS } from '@/constants/reactQueryKeys'

export interface RollbackInvoiceProps extends ModalProps {
    invoiceID: number
}

interface RollbackFormData {
    message: string
}

const RollbackInvoice: React.FC<RollbackInvoiceProps> = ({
    open,
    onClose,
    invoiceID,
}) => {
    const { t } = useTranslation()
    const { control, handleSubmit, reset } = useForm<RollbackFormData>({
        defaultValues: {
            message: '',
        },
    })

    const { refetch } = useQueryClientCallback({
        mode: 'contains',
        pattern: QUERY_KEYS.INVOICES,
    })

    const { mutateAsync: rollbackMutation, isPending } = useMutationWithHandler(
        {
            mutationFn: rollbackInvoice,
        },
        {
            reactQueryKeys: QUERY_KEYS.INVOICES,
            successMessage: t('rollback-invoice-success'),
            onSuccess: () => {
                refetch()
                reset()
                onClose()
            },
        }
    )

    const onSubmitHandler = async (data: RollbackFormData) => {
        if (!data?.message?.trim()) return

        rollbackMutation({
            invoiceID,
            message: data.message,
        })
    }

    return (
        <Dialog open={open} onClose={onClose} title={t('rollback-invoice')}>
            <form
                onSubmit={handleSubmit(onSubmitHandler)}
                className="space-y-4"
            >
                <FormItem
                    control={control}
                    name="message"
                    type="textarea"
                    label={t('rollback-reason')}
                    placeholder={t('enter-rollback-reason')}
                />

                <div className="flex items-center justify-end gap-2">
                    <Button variant="outlined" onClick={onClose} type="button">
                        {t('cancel')}
                    </Button>
                    <Button
                        loading={isPending}
                        disabled={isPending}
                        type="submit"
                    >
                        {t('confirm')}
                    </Button>
                </div>
            </form>
        </Dialog>
    )
}

export default RollbackInvoice
